import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import './ProjectEstimate.scss';
import { useProjectScenarios } from '../../../context/ProjectScenariosContext';
import Accordion, { AccordionItem } from '../../common/Accordion/Accordion';
import { UserRoles } from '../../../constants/UserRoles';
import ScenarioSwitcher from './ScenarioSwitcher';
import { ProjectsScenariosRestDataProvider } from '../../../data/ProjectScenariosDataProvider';
import { FormFieldConfig } from '../../common/FormGenerator/FormGenerator';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { FaBrain } from "react-icons/fa";
import GenericFlyoutPanel from '../../common/GenericFlyoutPanel/GenericFlyoutPanel';
import ProjectEstimateAIAssistant from './ProjectEstimateAIAssistant';
import { useTranslation } from 'react-i18next';

// Helper to inject calculated value into each item
function injectCalculatedTotal(item: any) {
  return {
    ...item,
    multiplier: item.multiplier === undefined ? 1 : item.multiplier,
    total: ((Number(item.hours) || 0) * (Number(item.multiplier ?? 1) || 0)),
  };
}

const generateGuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });

interface ProjectEstimateProps {
  scenarioId: string;
  setScenarioId: (id: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const ProjectEstimate: React.FC<ProjectEstimateProps> = ({ scenarioId, setScenarioId, onDirtyChange }) => {
  const { t } = useTranslation(['common', 'screens']);
  const { scenarios, setScenarios } = useProjectScenarios();

  const columns: DetailedListColumn<any>[] = React.useMemo(() => [
    { key: 'activity', header: t('screens:projectEstimate.columns.activityName'), dataField: 'activity', type: 'string' },
    { key: 'hours', header: t('screens:projectEstimate.columns.estimatedHours'), dataField: 'hours', type: 'number' },
    { key: 'multiplier', header: t('screens:projectEstimate.columns.multiplier'), dataField: 'multiplier', type: 'number', defaultValue: 1 },
    {
      key: 'total',
      header: t('screens:projectEstimate.columns.total'),
      render: (item: any) => {
        const hours = Number(item.hours) || 0;
        const multiplier = Number(item.multiplier) || 0;
        return (hours * multiplier).toFixed(2);
      },
    },
  ], [t]);

  const projectEstimateFormFields = React.useMemo((): FormFieldConfig[] => [
    { key: 'activity', label: t('screens:projectEstimate.columns.activityName'), type: 'string', required: true },
    { key: 'hours', label: t('screens:projectEstimate.columns.estimatedHours'), type: 'number', required: true },
    { key: 'multiplier', label: t('screens:projectEstimate.columns.multiplier'), type: 'number', required: true, min: 0 },
  ], [t]);
  const [scenario, setScenario] = useState(() => scenarios.find(s => s.id === scenarioId) || scenarios[0]);
  const [saving, setSaving] = useState(false);
  const [phaseEditStates, setPhaseEditStates] = useState<{ [id: string]: { name: string; editing: boolean } }>({});
  const [phaseToDelete, setPhaseToDelete] = useState<string | null>(null);
  const [newPhaseName, setNewPhaseName] = useState('');
  const [addPhaseDialogOpen, setAddPhaseDialogOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const provider = React.useRef(new ProjectsScenariosRestDataProvider()).current;

  // Always update scenario when scenarios or scenarioId changes
  React.useEffect(() => {
    setScenario(scenarios.find(s => s.id === scenarioId) || scenarios[0]);
  }, [scenarios, scenarioId]);

  // Call onDirtyChange when isDirty changes
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  if (!scenario) return <div className="card-glass project-tab-section"><h4 className="section-title">{t('screens:projectEstimate.effortEstimation')}</h4>{t('screens:projects.noScenarioSelected')}</div>;

  // Group estimate items by phase
  const phases = scenario.ganttChartPlan?.phases || [];
  const getItemsByPhase = (estimateItems: any[]) => {
    const estimateItemsWithIds = estimateItems.map(item => ({ ...item, id: item.id || generateGuid() }));
    const itemsByPhase: { [phaseId: string]: any[] } = {};
    estimateItemsWithIds.forEach(item => {
      if (!itemsByPhase[item.phaseId]) itemsByPhase[item.phaseId] = [];
      itemsByPhase[item.phaseId].push(item);
    });
    return itemsByPhase;
  };
  const itemsByPhase = getItemsByPhase(scenario.estimateItems.map(injectCalculatedTotal));

  // All changes are immediately reflected in the scenario context
  const handleChange = (phaseId: string, newEstimates: any[]) => {
    setIsDirty(true);
    // Ensure all new items have a stable id and correct phaseId
    const withIds = newEstimates.map(item => ({ ...item, id: item.id || generateGuid(), phaseId }));
    // Flatten all items except for the current phase, then append the new items for the current phase
    const allOtherItems = scenario.estimateItems.filter(item => item.phaseId !== phaseId);
    setScenarios(scenarios.map(s =>
      s.id === scenario.id ? { ...s, estimateItems: [...allOtherItems, ...withIds] } : s
    ));
  };

  // Save handler for ScenarioSwitcher
  const handleSave = async () => {
    if (!scenario.id) return;
    setSaving(true);
    try {
      await provider.update(scenario.id, scenario);
    } finally {
      setSaving(false);
      setIsDirty(false);
    }
  };

  // --- Phase Management Handlers ---
  const handlePhaseNameChange = (id: string, name: string) => {
    setPhaseEditStates(prev => ({ ...prev, [id]: { ...(prev[id] || {}), name } }));
  };

  const handlePhaseEdit = (id: string) => {
    const phase = phases.find(p => p.id === id);
    setPhaseEditStates(prev => ({ ...prev, [id]: { name: phase?.name || '', editing: true } }));
  };

  const handlePhaseEditSave = (id: string) => {
    const newName = phaseEditStates[id]?.name.trim();
    if (!newName) return;
    setScenarios(scenarios.map(s =>
      s.id === scenario.id
        ? {
            ...s,
            ganttChartPlan: {
              ...s.ganttChartPlan,
              phases: s.ganttChartPlan.phases.map((p: any) => p.id === id ? { ...p, name: newName } : p)
            }
          }
        : s
    ));
    setPhaseEditStates(prev => ({ ...prev, [id]: { name: newName, editing: false } }));
  };

  const handlePhaseEditCancel = (id: string) => {
    setPhaseEditStates(prev => ({ ...prev, [id]: { ...prev[id], editing: false } }));
  };

  const handlePhaseDelete = (id: string) => {
    setPhaseToDelete(id);
  };

  const confirmPhaseDelete = () => {
    if (!phaseToDelete) return;
    setScenarios(scenarios.map(s =>
      s.id === scenario.id
        ? {
            ...s,
            ganttChartPlan: {
              ...s.ganttChartPlan,
              phases: s.ganttChartPlan.phases.filter((p: any) => p.id !== phaseToDelete)
            },
            estimateItems: s.estimateItems.filter((item: any) => item.phaseId !== phaseToDelete)
          }
        : s
    ));
    setPhaseEditStates(prev => {
      const copy = { ...prev };
      delete copy[phaseToDelete!];
      return copy;
    });
    setPhaseToDelete(null);
  };

  const cancelPhaseDelete = () => setPhaseToDelete(null);

  const handlePhaseAdd = () => {
    const name = newPhaseName.trim();
    if (!name) return;
    const newId = generateGuid();
    const newPhase = {
      id: newId,
      name,
      order: phases.length,
      resources: [],
    };
    setScenarios(scenarios.map(s =>
      s.id === scenario.id
        ? {
            ...s,
            ganttChartPlan: {
              ...s.ganttChartPlan,
              phases: [...s.ganttChartPlan.phases, newPhase]
            }
          }
        : s
    ));
    setNewPhaseName('');
    setAddPhaseDialogOpen(false);
  };

  const movePhase = (id: string, direction: -1 | 1) => {
    const idx = phases.findIndex(p => p.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= phases.length) return;
    const newPhases = [...phases];
    const [removed] = newPhases.splice(idx, 1);
    newPhases.splice(newIdx, 0, removed);
    setScenarios(scenarios.map(s =>
      s.id === scenario.id
        ? {
            ...s,
            ganttChartPlan: {
              ...s.ganttChartPlan,
              phases: newPhases
            }
          }
        : s
    ));
  };


  // Drag-and-drop handler
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const newPhases = Array.from(phases);
    const [removed] = newPhases.splice(from, 1);
    newPhases.splice(to, 0, removed);
    setScenarios(scenarios.map(s =>
      s.id === scenario.id
        ? {
            ...s,
            ganttChartPlan: {
              ...s.ganttChartPlan,
              phases: newPhases
            }
          }
        : s
    ));
  };

  // --- Accordion expand/collapse state ---
  const [expandedPhases, setExpandedPhases] = useState<{ [id: string]: boolean }>({});
  const togglePhase = (id: string) => setExpandedPhases(prev => ({ ...prev, [id]: !prev[id] }));

  // Render phases as draggable and foldable accordion items
  const renderAccordionItems = () => (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="phases-droppable">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {phases.map((phase, i) => (
              <Draggable key={phase.id} draggableId={phase.id} index={i}>
                {(dragProvided, dragSnapshot) => {
                  const expanded = expandedPhases[phase.id];
                  return (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={expanded ? 'phase-accordion-section' : undefined}
                      style={{
                        ...dragProvided.draggableProps.style,
                        marginBottom: 8,
                        background: dragSnapshot.isDragging ? '#f3f0fa' : undefined,
                        borderRadius: 8,
                        boxShadow: dragSnapshot.isDragging ? '0 2px 8px rgba(123,63,228,0.10)' : undefined,
                      }}
                    >
                      <div
                        className="phase-header phase-header-hoverable"
                        style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', width: '100%', position: 'relative', minHeight: 44 }}
                        onClick={e => {
                          // Only toggle if not clicking drag handle or a button
                          if ((e.target as HTMLElement).closest('.phase-header-btns') || (e.target as HTMLElement).closest('.phase-drag-handle')) return;
                          togglePhase(phase.id);
                        }}
                      >
                        <span
                          {...dragProvided.dragHandleProps}
                          className="phase-drag-handle"
                          style={{ cursor: 'grab', marginRight: 8, color: '#bbb', fontSize: 18, userSelect: 'none' }}
                          onClick={e => e.stopPropagation()}
                        >☰</span>
                        {phaseEditStates[phase.id]?.editing ? (
                          <>
                            <input
                              type="text"
                              className="phase-edit-input"
                              value={phaseEditStates[phase.id]?.name}
                              onChange={e => handlePhaseNameChange(phase.id, e.target.value)}
                              onBlur={() => handlePhaseEditSave(phase.id)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handlePhaseEditSave(phase.id);
                                if (e.key === 'Escape') handlePhaseEditCancel(phase.id);
                              }}
                              autoFocus
                              style={{  }}
                              onClick={e => e.stopPropagation()}
                            />
                            <span style={{ flex: 1 }} />
                            <div className="phase-header-btns phase-header-btns-visible" style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                              <button onClick={e => { e.stopPropagation(); handlePhaseEditSave(phase.id); }} style={{}}>✔</button>
                              <button onClick={e => { e.stopPropagation(); handlePhaseEditCancel(phase.id); }} style={{}}>✖</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <span style={{ fontWeight: 800, fontSize: 16 }}>{phase.name}</span>
                            <span style={{ flex: 1 }} />
                            <div className="phase-header-btns" style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                              <button onClick={e => { e.stopPropagation(); handlePhaseEdit(phase.id); }} title="Rename">✎</button>
                              <button onClick={e => { e.stopPropagation(); movePhase(phase.id, -1); }} disabled={i === 0} title="Move up">↑</button>
                              <button onClick={e => { e.stopPropagation(); movePhase(phase.id, 1); }} disabled={i === phases.length - 1} title="Move down">↓</button>
                              <button onClick={e => { e.stopPropagation(); handlePhaseDelete(phase.id); }} title="Delete" style={{ color: '#c00' }}>🗑</button>
                            </div>
                          </>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); togglePhase(phase.id); }}
                          style={{ marginLeft: 8, background: 'none', border: 'none', color: '#bbb', fontSize: 18, cursor: 'pointer', padding: 0 }}
                          title={expandedPhases[phase.id] ? 'Collapse' : 'Expand'}
                          tabIndex={-1}
                          aria-label={expandedPhases[phase.id] ? 'Collapse section' : 'Expand section'}
                        >
                          {expandedPhases[phase.id] ? '▾' : '▸'}
                        </button>
                      </div>
                      {expanded && (
                        <DetailedListComponent
                          dataSource={{
                            data: (itemsByPhase[phase.id] || []).map(injectCalculatedTotal),
                            setData: (data: any[]) => handleChange(phase.id, data.map(item => ({ ...item, phaseId: phase.id })) )
                          }}
                          columns={columns}
                          rowKey={(item: any) => item.id}
                          addRole={UserRoles.Admin + ", " + UserRoles.Manager}
                          editRole={UserRoles.Admin + ", " + UserRoles.Manager}
                          deleteRole={UserRoles.Admin + ", " + UserRoles.Manager}
                          formFields={projectEstimateFormFields}
                        />
                      )}
                    </div>
                  );
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );

  return (
    <>
      <ScenarioSwitcher scenarioId={scenario?.id} setScenarioId={setScenarioId} onSave={handleSave} saving={saving} />
      <div className="card-glass project-tab-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h4 className="section-title" style={{ marginBottom: 0 }}>{t('screens:projectEstimate.effortEstimation')}</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn btn-primary"
              style={{ fontWeight: 600, fontSize: 18, padding: '2px 16px', borderRadius: 8 }}
              onClick={() => setAddPhaseDialogOpen(true)}
              title="Add Phase"
            >＋</button>
            <button
              className="btn"
              style={{ background: 'linear-gradient(90deg, #7b3fe4 0%, #a259ec 100%)', color: 'white', borderRadius: 8, fontSize: 20, padding: '4px 12px', display: 'flex', alignItems: 'center', border: 'none', boxShadow: '0 1px 4px rgba(123,63,228,0.10)' }}
              title="AI Review"
              onClick={() => setShowAIAssistant(true)}
            >
              <FaBrain style={{ marginRight: 6, fontSize: 22 }} />
            </button>
          </div>
        </div>
        <InfoPanel header={t('screens:projectEstimate.effortEstimation')}>
            This section allows you to manage the estimates for the effort required to complete the project. You can manage both the phases for this project as well as the individual activities
            within each phase. The estimates are used to calculate the total effort required for the project, which can be used for planning and resource allocation.
        </InfoPanel>
        {phases.length === 0 && <div>{t('screens:projectEstimate.noPhases')}</div>}
        {renderAccordionItems()}
      </div>
      {/* Add Phase dialog */}
      <PopupDialog
        open={addPhaseDialogOpen}
        title={t('screens:projectEstimate.addNewPhase')}
        onClose={() => setAddPhaseDialogOpen(false)}
        actions={[
          <button key="cancel" onClick={() => setAddPhaseDialogOpen(false)}>{t('common:cancel')}</button>,
          <button key="add" onClick={handlePhaseAdd} disabled={!newPhaseName.trim()} style={{ fontWeight: 600, color: '#6610f2' }}>{t('common:add')}</button>
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 260 }}>
          <label htmlFor="new-phase-name" style={{ fontWeight: 500, marginBottom: 4 }}>{t('screens:projectEstimate.phaseName')}</label>
          <input
            id="new-phase-name"
            type="text"
            className="phase-edit-input"
            placeholder={t('screens:projectEstimate.enterPhaseName')}
            value={newPhaseName}
            onChange={e => setNewPhaseName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && newPhaseName.trim()) handlePhaseAdd();
            }}
            autoFocus
          />
        </div>
      </PopupDialog>
      {/* Delete confirmation dialog */}
      <PopupDialog
        open={!!phaseToDelete}
        title={t('screens:projectEstimate.deletePhase')}
        onClose={cancelPhaseDelete}
        actions={[
          <button key="cancel" onClick={cancelPhaseDelete}>{t('common:cancel')}</button>,
          <button key="delete" onClick={confirmPhaseDelete} style={{ color: '#c00', fontWeight: 600 }}>{t('common:delete')}</button>
        ]}
      >
        {t('screens:projectEstimate.confirmDeletePhase')}
      </PopupDialog>
      {/* AI Assistant Flyout Panel */}
      <GenericFlyoutPanel open={showAIAssistant} onClose={() => setShowAIAssistant(false)} title="AI Assistant">
        <ProjectEstimateAIAssistant />
      </GenericFlyoutPanel>
    </>
  );
};

export default ProjectEstimate;
